import { Alert, Button, Card, Empty, Input, List, Space, Spin, Tag, Typography, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import MarkdownContent, { extractMarkdownHeadings } from '../components/MarkdownContent';
import { getHelpArticle, listHelpArticles } from '../services/api';
import type { HelpArticle } from '../types/incident';

const { Paragraph, Text } = Typography;

const matchesQuery = (article: HelpArticle, query: string): boolean => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return [
    article.title,
    article.summary || '',
    article.category,
    article.body_markdown,
    ...article.keywords,
  ].some((value) => value.toLowerCase().includes(normalized));
};

const HelpPage = () => {
  const { slug } = useParams<{ slug?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [article, setArticle] = useState<HelpArticle | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (slug) {
        setArticle(await getHelpArticle(slug));
        setArticles([]);
      } else {
        setArticles(await listHelpArticles());
        setArticle(null);
      }
    } catch {
      messageApi.error(t('help.messages.loadFailed'));
      setArticle(null);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [messageApi, slug, t]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredArticles = useMemo(
    () => articles.filter((item) => matchesQuery(item, query)),
    [articles, query]
  );

  const groupedArticles = useMemo(() => {
    const groups = new Map<string, HelpArticle[]>();
    filteredArticles.forEach((item) => {
      groups.set(item.category, [...(groups.get(item.category) || []), item]);
    });
    return Array.from(groups.entries());
  }, [filteredArticles]);

  const articleHeadings = useMemo(
    () => (article ? extractMarkdownHeadings(article.body_markdown) : []),
    [article]
  );

  useEffect(() => {
    if (loading || !article || !location.hash) return;
    const rawHash = location.hash.slice(1);
    const targetId = decodeURIComponent(rawHash);
    window.setTimeout(() => {
      const target = document.getElementById(targetId) || document.getElementById(rawHash);
      target?.scrollIntoView({ block: 'start' });
    }, 0);
  }, [article, loading, location.hash]);

  if (loading) {
    return (
      <div className="page helpPage">
        {contextHolder}
        <Spin />
      </div>
    );
  }

  if (slug) {
    if (!article) {
      return (
        <div className="page helpPage">
          {contextHolder}
          <Alert type="warning" showIcon message={t('help.notFound')} />
        </div>
      );
    }
    return (
      <div className="page helpPage">
        {contextHolder}
        <Space className="backRow">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/help')}>
            {t('help.actions.backToList')}
          </Button>
        </Space>
        <div className="helpArticleLayout">
          <Card className="helpArticle">
            <Space direction="vertical" size={6}>
              <Tag>{article.category}</Tag>
              <h1>{article.title}</h1>
              {article.summary && <Paragraph type="secondary">{article.summary}</Paragraph>}
            </Space>
            <MarkdownContent content={article.body_markdown} />
          </Card>
          {articleHeadings.length > 0 && (
            <Card className="helpTocCard" title={t('help.tocTitle')}>
              <nav className="helpToc">
                {articleHeadings.map((heading, index) => (
                  <a key={`${heading.id}-${index}`} className={`helpTocLink helpTocLevel${heading.level}`} href={`#${heading.id}`}>
                    {heading.text}
                  </a>
                ))}
              </nav>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="page helpPage">
      {contextHolder}
      <div className="pageHeader pageHeaderRow">
        <p>{t('help.description')}</p>
        <Input.Search
          allowClear
          className="helpSearch"
          placeholder={t('help.searchPlaceholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {groupedArticles.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('help.empty')} />
      ) : (
        <div className="helpArticleList">
          {groupedArticles.map(([category, items]) => (
            <Card key={category} title={category}>
              <List
                dataSource={items}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button key="open" type="link" onClick={() => navigate(`/help/${item.slug}`)}>
                        {t('help.actions.open')}
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Button className="helpArticleTitleButton" type="link" onClick={() => navigate(`/help/${item.slug}`)}>
                          {item.title}
                        </Button>
                      }
                      description={
                        <Space direction="vertical" size={4}>
                          {item.summary && <Text type="secondary">{item.summary}</Text>}
                          {item.keywords.length > 0 && (
                            <Space size={[4, 4]} wrap>
                              {item.keywords.map((keyword) => (
                                <Tag key={keyword}>{keyword}</Tag>
                              ))}
                            </Space>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default HelpPage;
